import { toBook, type ApiBook } from './api';
import type { Book, Category, Rental, FavoriteItem } from '@/types/catalog';

// Client-side data helpers. Browser components call the fce-books-api through
// the same-origin /api/backend proxy (which injects the User-Agent and can
// attach the session cookie); they never reach the API host directly.
async function proxyGet<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`/api/backend/${path}`, { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) p.set(k, String(v));
  }
  return p.toString();
}

export async function clientListBooks(
  params: Record<string, string | number | undefined>,
): Promise<{ items: Book[]; total: number }> {
  const d = await proxyGet<{ items: ApiBook[]; total: number }>(`books?${qs(params)}`);
  return { items: (d?.items ?? []).map(toBook), total: d?.total ?? 0 };
}

export async function clientFacets(category?: string): Promise<{ authors: string[]; years: number[]; subjects: string[] }> {
  const d = await proxyGet<{ authors: string[]; years: number[]; subjects: string[] }>(
    `books/facets${category ? `?category=${encodeURIComponent(category)}` : ''}`,
  );
  return d ?? { authors: [], years: [], subjects: [] };
}

export async function clientCategories(): Promise<Category[]> {
  const d = await proxyGet<{ items: Category[] }>('categories');
  return d?.items ?? [];
}

export async function clientSearch(q: string): Promise<Book[]> {
  if (!q.trim()) return [];
  const d = await proxyGet<{ items: ApiBook[] }>(`search?q=${encodeURIComponent(q)}`);
  return (d?.items ?? []).map(toBook);
}

export async function clientGetBook(id: string): Promise<Book | null> {
  const d = await proxyGet<ApiBook>(`books/${encodeURIComponent(id)}`);
  return d ? toBook(d) : null;
}

// ── Authenticated helpers (session cookie → proxy injects the Bearer) ──

interface ApiRental {
  id: string;
  book_id: string;
  started_at: string;
  expires_at: string;
  returned_at?: string | null;
  title: string;
  author: string;
  cover_image_url?: string | null;
  pages?: number | null;
  year?: number | null;
  product?: string | null;
}

function toRental(r: ApiRental): Rental {
  return {
    id: r.id,
    bookId: r.book_id,
    startedAt: r.started_at,
    expiresAt: r.expires_at,
    returnedAt: r.returned_at ?? undefined,
    title: r.title,
    author: r.author,
    coverUrl: r.cover_image_url ?? undefined,
    pages: r.pages ?? undefined,
    year: r.year ?? undefined,
    product: r.product === 'audiobook' ? 'audiobook' : 'ebook',
  };
}

async function proxySend<T>(path: string, method: string, body?: unknown): Promise<{ ok: boolean; status: number; data: T | null }> {
  try {
    const res = await fetch(`/api/backend/${path}`, {
      method,
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = (await res.json().catch(() => null)) as T | null;
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  }
}

export async function clientActiveRentals(): Promise<Rental[]> {
  const d = await proxyGet<{ items: ApiRental[] }>('rentals/active');
  return (d?.items ?? []).map(toRental);
}

export async function clientRentalHistory(): Promise<Rental[]> {
  const d = await proxyGet<{ items: ApiRental[] }>('rentals/history');
  return (d?.items ?? []).map(toRental);
}

export type CheckoutResult =
  | { ok: true }
  | { ok: false; error: 'ACTIVE_RENTAL_EXISTS' | 'LIFETIME_CAP_REACHED' | 'unknown' };

export async function clientCheckout(bookId: string): Promise<CheckoutResult> {
  const r = await proxySend<{ error?: string }>('rentals/checkout', 'POST', { bookId });
  if (r.ok) return { ok: true };
  const err = r.data?.error;
  if (err === 'ACTIVE_RENTAL_EXISTS' || err === 'LIFETIME_CAP_REACHED') return { ok: false, error: err };
  return { ok: false, error: 'unknown' };
}

export async function clientReturnRental(rentalId: string): Promise<boolean> {
  const r = await proxySend(`rentals/${encodeURIComponent(rentalId)}/return`, 'POST', {});
  return r.ok;
}

export async function clientFavorites(): Promise<FavoriteItem[]> {
  const d = await proxyGet<{ items: Array<{ book_id: string; title: string; author: string; cover_image_url?: string | null; year?: number | null; subject?: string | null }> }>('favorites');
  return (d?.items ?? []).map((f) => ({
    bookId: f.book_id,
    title: f.title,
    author: f.author,
    coverUrl: f.cover_image_url ?? undefined,
    year: f.year ?? undefined,
    subject: f.subject ?? undefined,
  }));
}

export async function clientAddFavorite(bookId: string): Promise<boolean> {
  return (await proxySend('favorites', 'POST', { bookId })).ok;
}

export async function clientRemoveFavorite(bookId: string): Promise<boolean> {
  return (await proxySend(`favorites/${encodeURIComponent(bookId)}`, 'DELETE')).ok;
}

export async function clientChat(prompt: string): Promise<{ message: string } | null> {
  const r = await proxySend<{ message: string }>('chat', 'POST', { prompt });
  return r.ok ? r.data : null;
}

export async function clientProgressMap(): Promise<Record<string, number>> {
  const d = await proxyGet<{ items: Array<{ book_id: string; progress: number }> }>('progress');
  return Object.fromEntries((d?.items ?? []).map((i) => [i.book_id, i.progress]));
}

export interface Chapter {
  display_order: number;
  title: string;
  duration_ms?: number | null;
}

export async function clientChapters(bookId: string): Promise<Chapter[]> {
  const d = await proxyGet<{ items: Chapter[] }>(`books/${encodeURIComponent(bookId)}/chapters`);
  return d?.items ?? [];
}
